import { TrashIcon, ViewVerticalIcon } from "@radix-ui/react-icons";
import { AlertDialog, Button, Flex } from "@radix-ui/themes";
import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetSubscriptionsQuery } from "../../hooks/useGetSubscriptionsQuery";
import { setStudentData } from "../../redux/slices/school/student";
import { setSubscriptionData } from "../../redux/slices/school/subscription";
import { setUserData } from "../../redux/slices/users/user";
import { routes } from "../../routes";
import { getStudentDebtsBySubscription } from "../../services/finance/studentDebt";
import { getStudentById } from "../../services/school/student/getStudentByCode";
import { getSubjectsSubscriptions } from "../../services/school/student/getSubscriptions";
import { updateSubscription } from "../../services/school/student/updateSubscription";
import { getUserById } from "../../services/user/getUsers";
import { SubscriptionType } from "../../types/Student";
import { Key_subscriptions, PAGE_NUMBER, PAGE_SIZE } from "../../utils/constants";
import { stringToBoolean } from "../../utils/utilities";

const columnNamesMap: { [key: string]: string } = {
    student: 'Estudante',
    studentCode: 'Código',
    course: 'Curso',
    academicYear: 'Ano lectivo',
    level: 'Ano',
    semester: 'Semestre',
};

export const SubscriptionsValidation = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const [itemToBeDeleted, setItemToBeDeleted] = useState<SubscriptionType | null>(null);
    const { data, status } = useGetSubscriptionsQuery({ PageNumber: currentPage, PageSize: pageSize, SearchTerm: searchTerm, IdValidated: false });
    
    const mutationDelete = useMutation(updateSubscription, {
        onSuccess: () => {
            queryClient.invalidateQueries(Key_subscriptions);
            toast.success('Apagado');
        },
        onError: () => {
            toast.error("Erro ao apagar.")
        }
    });

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(PAGE_NUMBER);
    };

    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const handleClickCreate = (event: any): void => {
        navigate(routes.createSubscription)
    }
    const openDeleteDialog = (s: SubscriptionType) => {
        setItemToBeDeleted(s);
    };

    const confirmDelete = () => {
        if (itemToBeDeleted) {
            const updatedSubscription = { ...itemToBeDeleted, status: false };
            mutationDelete.mutateAsync(updatedSubscription);
            setItemToBeDeleted(null);
        }
    };

    const handleView = async (subscription: SubscriptionType) => {

        try {
            const studentData = await getStudentById(subscription.idStudent);
            if (!studentData) {
                return;
            }
            const userData = await getUserById(studentData.idUser)
            if (!userData) {
                toast.error("utilizador não encontrado.");
                return;
            }
            const data = await getSubjectsSubscriptions({
                PageSize: 400,
                PageNumber: 1,
                IdSubscription: subscription.id,
            }
            );
            subscription.courseSubjects = data?.items as any
            subscription.planSubjects = data?.items as any
            try {
                const studentDebtsResponse = await getStudentDebtsBySubscription(subscription.id!);
                const paymentsReferences = studentDebtsResponse
                    .flatMap((sdr) => sdr.paymentReferences || [])
                    .filter((ref) => ref?.id)
                    .filter((ref) => ref.status);
                subscription.paymentsReferences = paymentsReferences;
                const paymentsTransactions = studentDebtsResponse
                    .flatMap((sdr) => sdr.paymentTransactions || [])
                    .filter((ref) => ref?.id)
                    .filter((ref) => ref.status);
                subscription.paymentsTransactions = paymentsTransactions;
                const studentDebtsAssociations = studentDebtsResponse?.map(sdr => { return { idStudentDebt: sdr?.id, idSubscription: sdr?.idSubscription, idEnrollment: sdr?.idEnrollment, price: sdr?.originalAmount } })
                subscription.studentDebtsAssociations = studentDebtsAssociations;
                const idsSubjectsArray = Array.from(
                    new Set(
                        studentDebtsResponse
                            ?.map((sdr) => JSON.parse(sdr?.idsSubjects || "[]"))
                            .flat()
                    )
                );
                subscription.idsSubjectsArray = idsSubjectsArray;
                const studentDebtData = studentDebtsResponse?.[0];
                const paymentReferences = studentDebtData?.paymentReferences;
                const paymentTransactions = studentDebtData?.paymentTransactions;
                const paymentTransaction = paymentTransactions?.find(p => p?.status);
                const paymentReference = paymentReferences?.find(p => p?.status);
                subscription.course = subscription?.course;
                subscription.idsSubjects = studentDebtData?.idsSubjects;
                subscription.payment = {
                    idEnrollment: studentDebtData?.idEnrollment,
                    entity: paymentReference?.entity,
                    reference: paymentReference?.referenceCode,
                    totalPrice: paymentReference?.amount,
                    paymentProof: paymentTransaction?.paymentProof,
                    idStudentDebt: studentDebtData?.id,
                    isPaymentByEntityReference: true,
                }

            } catch (error) {
                console.log({ error })
            }
            dispatch(setSubscriptionData(subscription));
            dispatch(setStudentData(studentData));
            dispatch(
                setUserData({
                    ...userData,
                    isForeign: stringToBoolean(studentData.isForeign)
                } as any
                ));
            navigate(routes.viewSubscription);
        } catch (err: any) {
            toast.error(err?.message);
        }
    }

    const options = data?.items.map(() => [
        {
            label: 'Visualizar / Validar',
            onClick: (s: SubscriptionType) => handleView(s),
            icon: <ViewVerticalIcon />
        },
        {
            label: 'Excluir',
            onClick: (s: SubscriptionType) => openDeleteDialog(s),
            icon: <TrashIcon />
        },
    ]) || [];

    return (
        <>
            <Lists
                status={status}
                TableComponent={() =>
                    <TableComponent
                        columns={Object.keys(columnNamesMap)}
                        items={data?.items}
                        options={options}
                        columnNamesMap={columnNamesMap}
                        totalPages={data?.totalPages || 1}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                        onSearch={handleSearch}
                        search={searchTerm}
                        totalItems={data?.totalItems || 0}
                    />
                }
                handleClickCreate={handleClickCreate}
            />
            {itemToBeDeleted && (
                <AlertDialog.Root open={!!itemToBeDeleted} onOpenChange={() => setItemToBeDeleted(null)} >
                    <AlertDialog.Content maxWidth="450px" >
                        <AlertDialog.Title>Esta operação é irreversível </AlertDialog.Title>
                        <Flex gap="3" mt="4" justify="end">
                            <AlertDialog.Cancel >
                                <Button variant="soft" color="gray">Cancelar</Button>
                            </AlertDialog.Cancel>
                            <AlertDialog.Action >
                                <Button variant="solid" color="red" onClick={confirmDelete}>
                                    Continuar
                                </Button>
                            </AlertDialog.Action>
                        </Flex>
                    </AlertDialog.Content>
                </AlertDialog.Root>
            )}
        </>
    );
}